import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ScanLine, TriangleAlert } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL, SCAN_URL } from "../lib/site";

const LINES = ["WE CARE ABOUT", "EVERY VISITOR.", "LAWSUITS DON'T."];

const lineVariants = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { delay: 0.15 + i * 0.16, duration: 1, ease: [0.16, 1, 0.3, 1] },
  }),
};

const ThreatCard = () => (
  <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.9, duration: 1, ease: [0.16, 1, 0.3, 1] }}
      className="relative overflow-hidden rounded-3xl border border-white/10 bg-panel/80 backdrop-blur-xl shadow-2xl shadow-black/60"
      data-testid="hero-threat-card"
    >
      <div className="flex items-center justify-between border-b border-white/10 px-5 py-3.5">
        <span className="font-mono text-[11px] tracking-[0.22em] text-slate-400">THREAT RADAR — 2027</span>
        <span className="relative flex h-2.5 w-2.5">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-alert opacity-70" />
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-alert" />
        </span>
      </div>
      <div className="space-y-5 p-5 sm:p-6">
        <div>
          <div className="h-2 rounded-full bg-white/5 overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: "64%" }}
              transition={{ delay: 1.4, duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
              className="h-full rounded-full bg-gradient-to-r from-brandamber to-alert"
            />
          </div>
        </div>

        <div className="rounded-xl border border-white/10 bg-ink/60 p-4">
          <p className="font-mono text-xs font-bold leading-relaxed text-slate-100">
            <span className="text-brandamber font-bold">94.8%</span> of websites fail basic accessibility checks.
            AI tools now flag non-compliant sites in seconds — expect{" "}
            <span className="text-brandamber font-bold">7,000+</span> lawsuits in 2027.
          </p>
          <iframe
            data-testid="hero-news-video"
            className="mt-3 aspect-video w-full rounded-lg border-0"
            src="https://www.youtube.com/embed/3uM66zko3wM?si=mUrCYzmDURGlvLJ7"
            title="YouTube video player"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
          />
        </div>
      </div>
    </motion.div>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const cardY = useTransform(scrollYProgress, [0, 1], [0, 70]);

  return (
    <section ref={ref} id="top" className="relative min-h-screen overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-10">
        <img
          src="https://images.unsplash.com/photo-1639503547276-90230c4a4198?auto=format&fit=crop&w=1800&q=80"
          alt=""
          className="h-full w-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/85 to-ink" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_20%_20%,rgba(0,229,255,0.12),transparent_55%),radial-gradient(ellipse_at_80%_70%,rgba(255,59,48,0.1),transparent_50%)]" />
      </motion.div>

      <div className="pointer-events-none absolute inset-x-0 h-24 -z-[5] animate-scanbeam bg-gradient-to-b from-transparent via-cy/10 to-transparent" />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-[3px] -z-[5] animate-scanbeam bg-gradient-to-r from-transparent via-cy/60 to-transparent" />

      <div className="mx-auto grid max-w-7xl gap-12 px-5 pt-36 pb-20 sm:px-8 lg:grid-cols-[1.05fr_0.95fr] lg:items-center lg:gap-10 lg:pt-44 lg:pb-28">
        <div>
          <motion.h2
            initial={{ opacity: 0, y: -14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            data-testid="hero-demand-headline"
            className="font-display text-[clamp(1.6rem,7vw,2rem)] sm:text-5xl lg:text-[3.4rem] leading-[1] text-white"
          >
            DON'T GET CAUGHT WAITING'
            <br />
            <span
              className="inline-block font-black text-alert"
              style={{ textShadow: "0 0 34px rgba(255,59,48,0.45)" }}
            >
              50K+
            </span>{" "}
            <span className="text-alert">DEMAND LETTER.</span>
          </motion.h2>

          <motion.img
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            src="/ada-meme-2.jpg"
            alt="Cartoon: a stressed business owner wonders 'What's an ADA accessible website?' while a smiling 'Accessibility Lawsuit Specialist' says 'Got another one' — Results. Not excuses."
            data-testid="hero-meme-image"
            className="mt-6 w-full max-w-md rounded-2xl border border-white/15 shadow-2xl shadow-black/50"
          />

          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-brandamber/40 bg-brandamber/10 px-4 py-2"
          >
            <TriangleAlert className="h-3.5 w-3.5 text-brandamber" />
            <span className="animate-blink font-mono text-xs sm:text-sm font-extrabold tracking-[0.18em] text-brandamber">
              URGENT: 7,000+ ADA LAWSUITS PROJECTED IN 2027
            </span>
          </motion.div>

          <h1 className="mt-7 font-display text-[13.5vw] leading-[0.98] tracking-tight text-white sm:text-7xl lg:text-[5.4rem]">
            {LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${i === 2 ? "text-alert" : ""}`}
                  custom={i}
                  variants={lineVariants}
                  initial="hidden"
                  animate="show"
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.8 }}
            className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-slate-300"
          >
            Get your website <span className="text-white font-semibold">scanned for FREE</span> — a plain-English score,
            real code fixes and an accessibility toolbar in 132 languages. Before a demand letter finds you first.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.8 }}
            className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center"
          >
            <a
              data-testid="hero-free-scan-button"
              href={SCAN_URL}
              target="_blank"
              rel="noopener noreferrer sponsored"
              className="group inline-flex items-center justify-center gap-3 rounded-full bg-brandamber px-8 py-4 text-base font-bold text-ink shadow-lg shadow-brandamber/25 transition-all hover:scale-[1.03] hover:shadow-brandamber/40 active:scale-95"
            >
              <ScanLine className="h-5 w-5 transition-transform group-hover:rotate-12" />
              Get My FREE Scan
            </a>
            <a
              data-testid="hero-click-to-call-button"
              href={PHONE_TEL}
              className="inline-flex items-center justify-center gap-3 rounded-full border border-cy/50 bg-cy/5 px-8 py-4 text-base font-semibold text-cy transition-all hover:bg-cy/15 hover:border-cy active:scale-95"
            >
              <Phone className="h-5 w-5" />
              Call {PHONE_DISPLAY}
            </a>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.3, duration: 0.8 }}
            className="mt-5 font-mono text-[11px] font-bold tracking-wider text-slate-300"
          >
            FREE · UP TO 25 PAGES · PLAIN-ENGLISH REPORT — NO COST, NO CATCH
          </motion.p>
        </div>

        <motion.div style={{ y: cardY }} className="lg:pl-6">
          <ThreatCard />
        </motion.div>
      </div>
    </section>
  );
};
